import React, { useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Tooltip,
  AppBar,
  Toolbar,
  IconButton,
  InputBase,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  keyframes,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import { alpha, useTheme } from '@mui/material/styles';
import { useAuth } from './AuthContext';
import { TopNavBar } from './shared/TopNavBar';
import LogoCarousel from './LogoCarousel';
import { AiAnimationSubNav, AiAnimationSubSection } from './aiAnimation/AiAnimationSubNav';
import { RoleImageGeneratorPanel } from './aiAnimation/RoleImageGeneratorPanel';
import { ScriptAnalysisPanel } from './aiAnimation/ScriptAnalysisPanel';
import AiVideoEditPage from './aiAnimation/AiVideoEditPage';
import {
  extractApiErrorMessage,
  extractVideoTaskId,
  normalizeVideoQueryResponse,
} from './aiAnimation/videoApi';
import {
  createEmptyVideoRow,
  getCollapsedRowMinHeight,
  getExpandControlPlacement,
  getRightActionColumnMetrics,
  isRowBusy,
  resetVideoRow,
  type VideoRow,
} from './aiAnimation/animationRows';
import { API_BASE } from './config';
import { PointsCheckDialog } from './shared/PointsCheckDialog';
import { usePointsCheck } from './shared/usePointsCheck';

// ---- 豆包去水印面板 ----
interface DoubaoImageItem { url?: string; [key: string]: unknown; }
interface DoubaoVideoResult { url?: string; width?: number; height?: number; poster_url?: string; definition?: string; [key: string]: unknown; }

/** 豆包/ByteDance CDN 视频走后端代理避免 CORS */
function toProxiedUrl(url: string | undefined): string {
  if (!url) return '';
  try {
    const { hostname } = new URL(url);
    const proxyDomains = ['doubao.com', 'bytedance.com', 'byteimg.com', 'tiktokcdn.com', 'aliyuncs.com', 'amazonaws.com'];
    if (proxyDomains.some(d => hostname.endsWith(d))) {
      return `${API_BASE}/api/proxy/ext-video?url=${encodeURIComponent(url)}`;
    }
  } catch {}
  return url;
}

const DoubaoPanel: React.FC = () => {
  const [tab, setTab] = useState<0 | 1>(0);
  const [inputUrl, setInputUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [images, setImages] = useState<DoubaoImageItem[]>([]);
  const [video, setVideo] = useState<DoubaoVideoResult | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const isShareUrl = (url: string) =>
    /doubao\.com\/(share|thread)/.test(url) || /qianwen\.com/.test(url);

  const handleParse = async () => {
    const url = inputUrl.trim();
    if (!url) return;
    // 前端拦截：普通聊天链接无法解析
    if (url.includes('doubao.com/chat/')) {
      setError('请粘贴「分享链接」，不是聊天链接。在豆包对话页点击右上角「分享」按钮，复制生成的链接再粘贴。');
      return;
    }
    setLoading(true); setError(''); setImages([]); setVideo(null);
    const endpoint = tab === 0 ? '/api/doubao/parse-image' : '/api/doubao/parse-video';
    try {
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        body: JSON.stringify({ url }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '解析失败');
      if (tab === 0) setImages(data.images || []);
      else setVideo(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '未知错误');
    } finally {
      setLoading(false);
    }
  };

  const copyUrl = async (url: string) => {
    await navigator.clipboard.writeText(url);
    setCopied(url); setTimeout(() => setCopied(null), 2000);
  };

  const download = (url: string, filename: string) => {
    const a = document.createElement('a'); a.href = url; a.download = filename;
    a.target = '_blank'; a.rel = 'noopener noreferrer'; a.click();
  };

  return (
    <Box sx={{ mt: 8, mb: 4, width: '100%', maxWidth: 960, mx: 'auto', px: { xs: 1, md: 2 } }}>
      <Paper elevation={0} sx={{ border: '1px solid #bfdbfe', borderRadius: 3, p: 3, bgcolor: 'white', mb: 3 }}>
        {/* 类型切换 */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
          {['提取图片', '提取视频'].map((label, i) => (
            <Button key={i} variant={tab === i ? 'contained' : 'outlined'} size="small"
              onClick={() => { setTab(i as 0 | 1); setImages([]); setVideo(null); setError(''); }}
              sx={{ borderRadius: 2, fontWeight: 700, ...(tab === i ? { bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' } } : {}) }}>
              {label}
            </Button>
          ))}
        </Box>
        {/* 操作提示 */}
        <Typography variant="body2" sx={{ color: '#64748b', mb: 2, fontSize: '0.8rem', lineHeight: 1.7 }}>
          ⚠️ {tab === 0 ? '手机豆包长按图片获取分享链接' : '手机豆包长按视频获取分享链接'}
        </Typography>
        {/* 输入栏 */}
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <TextField fullWidth size="small"
            placeholder="粘贴豆包分享链接，例如 https://www.doubao.com/share/…" value={inputUrl}
            onChange={(e) => setInputUrl(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleParse()}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.9rem' } }} />
          <Button variant="contained" onClick={handleParse} disabled={loading || !inputUrl.trim()}
            sx={{ minWidth: 90, borderRadius: 2, fontWeight: 700, bgcolor: '#2563eb', flexShrink: 0, '&:hover': { bgcolor: '#1d4ed8' } }}>
            {loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : '解析'}
          </Button>
        </Box>
        {error && (
          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#fef2f2', borderRadius: 2, border: '1px solid #fecaca' }}>
            <Typography sx={{ color: '#dc2626', fontSize: '0.875rem' }}>{error}</Typography>
          </Box>
        )}
      </Paper>

      {/* 图片结果 */}
      {tab === 0 && images.length > 0 && (
        <Paper elevation={0} sx={{ border: '1px solid #bfdbfe', borderRadius: 3, p: 3, bgcolor: 'white' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mb: 2 }}>共找到 {images.length} 张图片</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', md: 'repeat(4,1fr)' }, gap: 2 }}>
            {images.map((img, i) => {
              const url = img.url || (typeof img === 'string' ? img : '');
              return (
                <Box key={i} sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0', aspectRatio: '1', '&:hover .actions': { opacity: 1 } }}>
                  <Box component="img" src={url} alt={`图片${i + 1}`} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  <Box className="actions" sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, opacity: 0, transition: 'opacity 0.2s' }}>
                    <Tooltip title={copied === url ? '已复制!' : '复制链接'}>
                      <IconButton size="small" onClick={() => copyUrl(url)} sx={{ bgcolor: 'white', '&:hover': { bgcolor: '#f1f5f9' } }}>
                        <AutoAwesomeIcon fontSize="small" sx={{ color: '#2563eb' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="下载">
                      <IconButton size="small" onClick={() => download(url, `doubao_${i + 1}.jpg`)} sx={{ bgcolor: 'white', '&:hover': { bgcolor: '#f1f5f9' } }}>
                        <CloseIcon fontSize="small" sx={{ color: '#16a34a', transform: 'rotate(45deg)' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={{ position: 'absolute', top: 6, left: 6, bgcolor: 'rgba(0,0,0,0.5)', color: 'white', fontSize: '0.7rem', fontWeight: 700, px: 0.8, py: 0.2, borderRadius: 1 }}>{i + 1}</Box>
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      {/* 视频结果 */}
      {tab === 1 && video && video.url && (
        <Paper elevation={0} sx={{ border: '1px solid #bfdbfe', borderRadius: 3, p: 3, bgcolor: 'white' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mb: 2 }}>
            视频解析成功{video.width && video.height ? ` · ${video.width}×${video.height}` : ''}{video.definition ? ` · ${video.definition}` : ''}
          </Typography>
          <Box sx={{ borderRadius: 2, overflow: 'hidden', mb: 2, bgcolor: '#000' }}>
            <video controls poster={toProxiedUrl(video.poster_url)} style={{ width: '100%', maxHeight: 480, display: 'block' }}>
              <source src={toProxiedUrl(video.url)} />
            </video>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<AutoAwesomeIcon />} onClick={() => copyUrl(video.url!)} sx={{ borderRadius: 2, fontWeight: 600 }}>
              {copied === video.url ? '已复制!' : '复制链接'}
            </Button>
            <Button variant="contained" onClick={() => download(video.url!, 'doubao_video.mp4')}
              sx={{ borderRadius: 2, fontWeight: 600, bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' } }}>下载视频</Button>
          </Box>
        </Paper>
      )}

      {/* 空状态 */}
      {!loading && images.length === 0 && !video && !error && (
        <Box sx={{ textAlign: 'center', py: 8, color: '#94a3b8' }}>
          <Typography sx={{ fontSize: 48, mb: 1 }}>🎯</Typography>
          <Typography sx={{ fontWeight: 600 }}>粘贴豆包分享链接后点击解析</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>支持图片和视频，去除水印后直接下载</Typography>
        </Box>
      )}
    </Box>
  );
};
// ---- end DoubaoPanel ----

const shimmer = keyframes`
  0% { transform: translateX(-100%) skewX(-20deg); }
  100% { transform: translateX(200%) skewX(-20deg); }
`;

const dotJump = keyframes`
  0%, 60%, 100% { transform: translateY(0); opacity: 0.55; }
  30% { transform: translateY(-5px); opacity: 1; }
`;

const NewsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, loading, refreshUser } = useAuth();
  const { checkPoints, showDialog, dialogProps, closeDialog } = usePointsCheck();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [topTab, setTopTab] = useState<'animation' | 'video' | 'doubao'>('animation');
  const [sub, setSub] = useState<AiAnimationSubSection>('role');
  const [scriptInstance, setScriptInstance] = useState(0);
  const [sendToAnimationLoading, setSendToAnimationLoading] = useState(false);
  const [sendToAnimationError, setSendToAnimationError] = useState<string | null>(null);

  // 角色描述弹窗相关状态
  const [roleDescDialogOpen, setRoleDescDialogOpen] = useState(false);
  const [roleDescText, setRoleDescText] = useState('');
  const [currentEditingRole, setCurrentEditingRole] = useState<{ rowId: string; roleId: string } | null>(null);

  const [isGeneratingRoleDesc, setIsGeneratingRoleDesc] = useState(false);
  const [isGeneratingRoleImage, setIsGeneratingRoleImage] = useState(false);

  const handleGenerateRoleImage = async () => {
    if (!currentEditingRole || !roleDescText.trim()) return;

    // 积分检查：角色图片生成需要4积分
    if (!checkPoints(4)) {
      return;
    }

    setIsGeneratingRoleImage(true);
    try {
      const res = await fetch(`${API_BASE}/api/generate/role-image`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          prompt: roleDescText,
          model: "nano-banana-2", 
          aspect_ratio: "9:16",
          response_format: "url",
          image_size: "1K"
        }),
      });

      if (!res.ok) {
        throw new Error('生成图片失败');
      }

      const data = await res.json();
      const imageUrl = data.url;

      // Update the role with the new image URL
      setRows(prev => prev.map(row => {
          if (row.id === currentEditingRole.rowId) {
             return {
                 ...row,
                 roleCards: row.roleCards.map(role => {
                     if (role.id === currentEditingRole.roleId) {
                         return { ...role, imageDataUrl: imageUrl };
                     }
                     return role;
                 })
             };
          }
          return row;
      }));
      // 新增：刷新用户积分
      try { await refreshUser(); } catch { /* ignore */ }

      handleCloseRoleDescDialog();

    } catch (error) {
       console.error(error);
       alert('生成图片失败，请重试');
    } finally {
        setIsGeneratingRoleImage(false);
    }
  };

  // 一键生成角色描述
  const handleGenerateRoleDesc = async (rowId: string, roleId: string) => {
    // 1. 获取推文内容 (从 sessionStorage)
    const tweetText = sessionStorage.getItem('xgai.scriptAnalyze.inputText') || '';
    if (!tweetText.trim()) {
      alert('请先在脚本生成页面工作台输入推文内容，再生成角色描述词');
      return;
    }

    // 2. 获取角色名称
    const row = rows.find(r => r.id === rowId);
    if (!row) return;
    const roleCard = row.roleCards.find(c => c.id === roleId);
    if (!roleCard) return;
    const roleName = roleCard.name;

    if (!roleName || roleName === '未命名') {
       alert('请先设置角色名称');
       return;
    }

    // 积分检查：角色描述生成需要1积分
    if (!checkPoints(1)) {
      return;
    }

    setIsGeneratingRoleDesc(true);
    // 3. 调用后端接口
    try {
      const res = await fetch(`${API_BASE}/api/proxy/xgai/role-description`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({ tweet_text: tweetText, role_name: roleName }),
      });

      if (!res.ok) {
        throw new Error('生成失败');
      }

      const data = await res.json();
      console.log('Role description response:', data);
      // 4. 将结果填充到弹窗的文本框中
      setRoleDescText(data.description);
      
      // 5. 刷新用户积分
      try { await refreshUser(); } catch { /* ignore */ }
      
    } catch (error) {
      console.error(error);
      alert('生成角色描述失败，请重试');
    } finally {
        setIsGeneratingRoleDesc(false);
    }
  };

  const handleOpenRoleDescDialog = (rowId: string, roleId: string) => {
    setCurrentEditingRole({ rowId, roleId });
    // setRoleDescText(''); // 不要清空，可能用户想基于已有的修改，或者需要先获取已保存的? 
    // Wait, the user requirement implies generating NEW description. But usually inputs should preserve state if already entered? 
    // For now I will clear it as requested implicitly by "Replace empty text field with generated one" flow essentially. 
    // Existing code cleared it: setRoleDescText('');
    setRoleDescText(''); 
    setRoleDescDialogOpen(true);
  };

  const handleCloseRoleDescDialog = () => {
    setRoleDescDialogOpen(false);
    setCurrentEditingRole(null);
  };

  type NanoTaskCreateResponse = {
    task_id?: string;
  };

  type NanoTaskStatusResponse = {
    task_id?: string;
    status?: string;
    images?: string[];
  };

  const headers = useMemo(() => ['分镜脚本', '角色', '操作', '九宫格图像', '视频脚本', '生成视频', '操作'], []);
  const gridCols = useMemo(
    () =>
      'minmax(0, 1.05fr) minmax(0, 1.55fr) minmax(0, 0.55fr) minmax(0, 1.05fr) minmax(0, 1.05fr) minmax(0, 1.25fr) minmax(0, 0.55fr)',
    []
  );
  const tableLine = 'rgba(15, 23, 42, 0.18)';
  const gridBorder = `2px solid ${tableLine}`;

  const makeRow = (id: string): VideoRow => createEmptyVideoRow(id);

  const [rows, setRows] = useState<VideoRow[]>(() => {
    try {
      const saved = localStorage.getItem('ai_animation_rows');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to load saved rows', e);
    }
    return [makeRow('row-1'), makeRow('row-2')];
  });

  const nextRowId = useRef(
    rows.reduce((max, row) => {
      const match = row.id.match(/^row-(\d+)$/);
      if (match) {
        const num = parseInt(match[1], 10);
        return num >= max ? num + 1 : max;
      }
      return max;
    }, 3)
  );

  React.useEffect(() => {
    localStorage.setItem('ai_animation_rows', JSON.stringify(rows));
  }, [rows]);

  React.useEffect(() => {
    rows.forEach((row) => {
      const taskId = String(row.gridTaskId || '').trim();
      if (!taskId || row.gridImageUrl) return;
      if (gridPollTaskRef.current[row.id] === taskId) return;
      startGridTaskPolling(row.id, taskId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const [rowLoading, setRowLoading] = useState<Record<string, { roles?: boolean; video?: boolean; grid?: boolean }>>({});
  const [rowError, setRowError] = useState<Record<string, { roles?: string; video?: string; grid?: string }>>({});
  const [rowRoleExpanded, setRowRoleExpanded] = useState<Record<string, boolean>>({});
  const gridPollTaskRef = useRef<Record<string, string>>({});

  const setRowLoadingFlag = (rowId: string, key: 'roles' | 'video' | 'grid', value: boolean) => {
    setRowLoading((prev) => ({
      ...prev,
      [rowId]: {
        ...(prev[rowId] || {}),
        [key]: value,
      },
    }));
  };

  const setRowErrorText = (rowId: string, key: 'roles' | 'video' | 'grid', text: string | undefined) => {
    setRowError((prev) => ({
      ...prev,
      [rowId]: {
        ...(prev[rowId] || {}),
        [key]: text,
      },
    }));
  };

  const toggleRowRoleExpanded = (rowId: string) => {
    setRowRoleExpanded((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const JumpingDots: React.FC<{ color?: string }> = ({ color }) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'flex-end', gap: 0.45, lineHeight: 1 }}>
      {[0, 1, 2].map((i) => (
        <Box
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          component="span"
          sx={{
            width: 5,
            height: 5,
            borderRadius: '50%',
            bgcolor: color || 'currentColor',
            display: 'inline-block',
            animation: `${dotJump} 900ms ease-in-out infinite`,
            animationDelay: `${i * 120}ms`,
          }}
        />
      ))}
    </Box>
  );

  const updateRow = (rowId: string, updater: (row: VideoRow) => VideoRow) => {
    setRows((prev) => prev.map((r) => (r.id === rowId ? updater(r) : r)));
  };

  const addRow = () => {
    setRows((prev) => {
      const id = `row-${nextRowId.current}`;
      nextRowId.current += 1;
      return [...prev, makeRow(id)];
    });
  };

  const deleteRow = (rowId: string) => {
    setRows((prev) => prev.filter((r) => r.id !== rowId));
  };

  const clearRow = (rowId: string) => {
    if (isRowBusy(rowLoading[rowId])) return;
    const confirmed = window.confirm('确定清除这一行的全部内容吗？');
    if (!confirmed) return;

    delete gridPollTaskRef.current[rowId];
    Object.keys(fileInputRefs.current).forEach((key) => {
      if (key.startsWith(`${rowId}:`)) {
        delete fileInputRefs.current[key];
      }
    });

    setRowLoading((prev) => {
      if (!(rowId in prev)) return prev;
      const next = { ...prev };
      delete next[rowId];
      return next;
    });

    setRowError((prev) => {
      if (!(rowId in prev)) return prev;
      const next = { ...prev };
      delete next[rowId];
      return next;
    });

    setRowRoleExpanded((prev) => {
      if (!(rowId in prev)) return prev;
      const next = { ...prev };
      delete next[rowId];
      return next;
    });

    if (currentEditingRole?.rowId === rowId) {
      setCurrentEditingRole(null);
      setRoleDescDialogOpen(false);
      setRoleDescText('');
      setIsGeneratingRoleDesc(false);
      setIsGeneratingRoleImage(false);
    }

    updateRow(rowId, () => resetVideoRow({ id: rowId }));
  };

  const addRoleCard = (rowId: string) => {
    updateRow(rowId, (row) => {
      if (row.roleCards.length >= 11) return row;
      const parseRoleNum = (id: string) => {
        const m = id.match(/-role-(\d+)$/);
        return m ? Number(m[1]) : 0;
      };
      const nextIdx = Math.max(0, ...row.roleCards.map((c) => parseRoleNum(c.id))) + 1;
      return {
        ...row,
        roleCards: [...row.roleCards, { id: `${rowId}-role-${nextIdx}`, name: '未命名', imageDataUrl: null, voice: '' }],
      };
    });
  };

  const deleteRoleCard = (rowId: string, roleId: string) => {
    updateRow(rowId, (row) => {
      if (row.roleCards.length <= 1) return row;
      return { ...row, roleCards: row.roleCards.filter((c) => c.id !== roleId) };
    });
  };

  const setRoleName = (rowId: string, roleId: string, name: string) => {
    updateRow(rowId, (row) => ({
      ...row,
      roleCards: row.roleCards.map((c) => (c.id === roleId ? { ...c, name } : c)),
    }));
  };

  const setRoleVoice = (rowId: string, roleId: string, voice: string) => {
    updateRow(rowId, (row) => ({
      ...row,
      roleCards: row.roleCards.map((c) => (c.id === roleId ? { ...c, voice } : c)),
    }));
  };

  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const setRoleImage = async (rowId: string, roleId: string, file: File) => {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('读取图片失败'));
      reader.onload = () => resolve(String(reader.result));
      reader.readAsDataURL(file);
    });
    updateRow(rowId, (row) => ({
      ...row,
      roleCards: row.roleCards.map((c) => (c.id === roleId ? { ...c, imageDataUrl: dataUrl } : c)),
    }));
  };

  const setStoryboardScript = (rowId: string, storyboardScript: string) => {
    updateRow(rowId, (row) => ({ ...row, storyboardScript }));
  };

  const setVideoScript = (rowId: string, videoScript: string) => {
    updateRow(rowId, (row) => ({ ...row, videoScript }));
  };

  const buildAuthHeaders = (withJson: boolean): Record<string, string> => {
    const headers: Record<string, string> = {};
    if (withJson) headers['Content-Type'] = 'application/json';
    const token = localStorage.getItem('access_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  };

  const pollNanoGridTask = async (rowId: string, taskId: string): Promise<string[]> => {
    const startAt = Date.now();
    const timeoutMs = 15 * 60 * 1000;

    while (Date.now() - startAt < timeoutMs) {
      if (gridPollTaskRef.current[rowId] !== taskId) return [];

      const res = await fetch(`${API_BASE}/api/proxy/nano/task/${encodeURIComponent(taskId)}`, {
        headers: buildAuthHeaders(false),
      });

      if (!res.ok) {
        const msg = await res.text().catch(() => '');
        throw new Error(msg || 'NanoBanana 查询失败');
      }

      const data = (await res.json()) as NanoTaskStatusResponse;
      const status = String(data.status || '').toLowerCase();
      const urls = (data.images || []).filter(Boolean);

      if (status === 'succeeded' || status === 'success' || status === 'completed') {
        if (urls.length > 0) return urls;
        throw new Error('任务已完成，但未返回图片');
      }

      if (status === 'failed' || status === 'fail' || status === 'failure') {
        throw new Error('NanoBanana 生成失败');
      }

      await new Promise((r) => setTimeout(r, 1500));
    }

    throw new Error('NanoBanana 生成超时');
  };

  const startGridTaskPolling = (rowId: string, taskId: string) => {
    gridPollTaskRef.current[rowId] = taskId;
    setRowLoadingFlag(rowId, 'grid', true);
    setRowErrorText(rowId, 'grid', undefined);

    (async () => {
      try {
        const urls = await pollNanoGridTask(rowId, taskId);
        if (!urls.length) throw new Error('NanoBanana 未返回图片');

        updateRow(rowId, (r) => {
          if (gridPollTaskRef.current[rowId] !== taskId) return r;
          return { ...r, gridImageUrl: urls[0], gridTaskId: null };
        });
      } catch (e: any) {
        const msg = e?.message || '九宫格生成失败';
        setRowErrorText(rowId, 'grid', msg);
        updateRow(rowId, (r) => {
          if (gridPollTaskRef.current[rowId] !== taskId) return r;
          return { ...r, gridTaskId: null };
        });
      } finally {
        if (gridPollTaskRef.current[rowId] === taskId) {
          delete gridPollTaskRef.current[rowId];
        }
        setRowLoadingFlag(rowId, 'grid', false);
      }
    })();
  };

  const streamTextPost = async (path: string, body: unknown, onChunk?: (chunk: string) => void): Promise<string> => {
    const res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: buildAuthHeaders(true),
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `请求失败（HTTP ${res.status}）`);
    }

    if (!res.body) {
      const text = await res.text().catch(() => '');
      if (onChunk && text) onChunk(text);
      return text;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let full = '';
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      if (!chunk) continue;
      full += chunk;
      onChunk?.(chunk);
    }
    return full;
  };

  type ExtractedRole = {
    name: string;
    voiceRaw: string;
    voiceType?: string;
    speed?: string;
    tone?: string;
    volume?: string;
  };

  const extractRolesFromAnalysis = (analysisText: string): ExtractedRole[] => {
    const text = (analysisText || '')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/\*\*/g, '');
    const matches: Array<{ index: number; full: string; name: string }> = [];
    // 只把“行首的【角色名】”当作角色分段，避免正文里出现【】导致串位
    const re = /(^|\n)([ \t]*)【([^】\n]+)】/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      const leadingNewline = m[1] || '';
      const leadingSpaces = m[2] || '';
      const name = (m[3] || '').trim();
      const index = (m.index ?? 0) + leadingNewline.length + leadingSpaces.length;
      const full = `【${name}】`;
      matches.push({ index, full, name });
    }
    if (!matches.length) return [];

    const roles: ExtractedRole[] = [];
    for (let i = 0; i < matches.length; i += 1) {
      const name = matches[i]?.name || '';
      const start = (matches[i].index ?? 0) + matches[i].full.length;
      const end = i + 1 < matches.length ? (matches[i + 1].index ?? text.length) : text.length;
      const section = text.slice(start, end);

      // 现在提示词只要求“音色设计”，因此音色格子只保留该部分内容
      const norm = section.trim();
      const idx1 = norm.search(/(?:①\s*)?音色设计\s*[:：]/);
      const voiceRaw = (idx1 >= 0 ? norm.slice(idx1) : norm)
        .replace(/^[-*\s]+/gm, '')
        .trim();

      const getParam = (key: string) => {
        const m = voiceRaw.match(new RegExp(`${key}\\s*(?:=|[:：])\\s*([^;；\n]+)`, 'i'));
        return m?.[1]?.trim();
      };

      const role: ExtractedRole = {
        name,
        voiceRaw,
        voiceType: getParam('音色类型'),
        speed: getParam('语速'),
        tone: getParam('语气'),
        volume: getParam('音量'),
      };

      roles.push(role);
    }
    return roles.filter((r) => r.name);
  };

  const applyRolesToRow = (rowId: string, roles: ExtractedRole[]) => {
    if (!roles.length) return;
    updateRow(rowId, (row) => {
      const nextRoleCards = row.roleCards.slice();

      const parseRoleNum = (id: string) => {
        const m = id.match(/-role-(\d+)$/);
        return m ? Number(m[1]) : 0;
      };
      let nextIdx = Math.max(0, ...nextRoleCards.map((c) => parseRoleNum(c.id))) + 1;

      while (nextRoleCards.length < roles.length && nextRoleCards.length < 11) {
        nextRoleCards.push({ id: `${rowId}-role-${nextIdx}`, name: '未命名', imageDataUrl: null, voice: '' });
        nextIdx += 1;
      }

      for (let i = 0; i < Math.min(roles.length, nextRoleCards.length); i += 1) {
        const role = roles[i];
        const voice = role.voiceRaw || '';
        nextRoleCards[i] = { ...nextRoleCards[i], name: role.name || nextRoleCards[i].name, voice };
      }

      return { ...row, roleCards: nextRoleCards };
    });
  };

  const applyAllToFirstEmptyRow = (payload: { storyboardScript: string; roles: ExtractedRole[]; videoScript: string }): string => {
    const { storyboardScript, roles, videoScript } = payload;
    const isEmpty = (s: string) => !String(s || '').trim();

    // 先基于当前 rows 选择目标行（避免依赖 setState updater 的副作用返回值）
    let chosenId: string | undefined = rows.find((r, idx) => idx === 0 && isEmpty(r.storyboardScript))?.id;
    if (!chosenId) chosenId = rows.find((r) => isEmpty(r.storyboardScript))?.id;
    const finalId = chosenId || `row-${nextRowId.current}`;
    if (!chosenId) nextRowId.current += 1;

    setRows((prev) => {
      const next = prev.slice();
      let idx = next.findIndex((r) => r.id === finalId);
      if (idx < 0) {
        next.push(makeRow(finalId));
        idx = next.length - 1;
      }
      const base = next[idx];
      let nextRoleCards = base.roleCards.slice();

      // 自动补足角色卡到最多 11 个
      const parseRoleNum = (id: string) => {
        const m = id.match(/-role-(\d+)$/);
        return m ? Number(m[1]) : 0;
      };
      let nextIdx = Math.max(0, ...nextRoleCards.map((c) => parseRoleNum(c.id))) + 1;
      while (nextRoleCards.length < roles.length && nextRoleCards.length < 11) {
        nextRoleCards.push({ id: `${finalId}-role-${nextIdx}`, name: '未命名', imageDataUrl: null, voice: '' });
        nextIdx += 1;
      }
      for (let i = 0; i < Math.min(roles.length, nextRoleCards.length); i += 1) {
        const role = roles[i];
        nextRoleCards[i] = { ...nextRoleCards[i], name: role.name || nextRoleCards[i].name, voice: role.voiceRaw || '' };
      }

      next[idx] = {
        ...base,
        storyboardScript,
        roleCards: nextRoleCards,
        videoScript,
      };
      return next;
    });

    return finalId;
  };

  const runRoleVoiceAnalysis = async (tweetText: string): Promise<ExtractedRole[]> => {
    const full = await streamTextPost('/api/proxy/xgai/role-voice-analyze', { tweet_text: tweetText });
    return extractRolesFromAnalysis(full);
  };

  const buildVideoScriptPromptFromRoles = (roles: ExtractedRole[]) => {
    const items = (roles || []).filter(Boolean).slice(0, 6); // 防止过长
    const roleLines = items
      .map((r, idx) => {
        const name = (r?.name || `角色${idx + 1}`).trim() || `角色${idx + 1}`;
        const voiceType = (r?.voiceType || '青年音/清冷少女音等').trim() || '青年音/清冷少女音等';
        const speed = (r?.speed || '中速').trim() || '中速';
        const tone = (r?.tone || '沉稳').trim() || '沉稳';
        const volume = (r?.volume || '中').trim() || '中';
        return `角色${idx + 1}（${name}）：${voiceType}，语速${speed}（中速/稍慢等），语气${tone}（沉稳/柔和等），音量${volume}（中/轻等）`;
      })
      .join('；');

    const roleBlock = roleLines ? `${roleLines}；` : '角色1（××）：青年音/清冷少女音等，语速××（中速/稍慢等），语气××（沉稳/柔和等），音量××（中/轻等）；';

    return (
      '基于上传的nanobanana 3×3九宫格分镜图，生成短视频。'
      + '要求：1. 视觉适配：严格按九宫格分镜顺序生成，每个镜头的角色、场景、动作、景别与九宫格100%一致，风格、色调、光影同步匹配，无画面偏差、跳帧或多余元素；'
      + '2. 音频要求：① 角色语音：按以下参数生成（可替换为角色形象生成提示词输出的对应角色参数）——'
      + `【${roleBlock}】`
      + '② 音画同步：口型与台词精准匹配，音频与画面同步误差≤0.2秒；'
      + '③ 环境与锚点：环境音符合场景设定（如溶洞滴水声），无多余音效；完整保留第9镜的上下集衔接音频锚点（××音效，留白××秒）；'
      + '3. 技术参数：分辨率1080×1920，帧率24fps，音频采样率44.1kHz、比特率256kbps；'
      + '4. 镜头节奏：单镜头时长0.8-1.2秒，转场自然无跳切，核心动作清晰突出，总时长控制在8-10秒，适配短视频传播。'
    );
  };

  const handleGenerateGridImages = async (rowId: string) => {
    const row = rows.find((r) => r.id === rowId);
    if (!row) return;

    const storyboard = (row.storyboardScript || '').trim();
    if (!storyboard) {
      window.alert('请先填写分镜脚本');
      return;
    }

    const filledRoles = row.roleCards.filter((card) => {
      const hasImg = Boolean(card.imageDataUrl);
      const name = (card.name || '').trim();
      const hasName = Boolean(name && name !== '未命名');
      return hasImg && hasName;
    });

    if (filledRoles.length === 0) {
      window.alert('请先上传角色垫图并为角色命名');
      return;
    }

    // Build the image list and role mapping string
    const validImages: string[] = [];
    const roleMappingParts: string[] = [];
    
    // Filter roles that have images
    let imgIndex = 0;
    filledRoles.forEach((card) => {
      validImages.push(card.imageDataUrl as string);
      imgIndex += 1;
      roleMappingParts.push(`第 ${imgIndex} 张上传图片→对应角色：${(card.name || '').trim()}`);
    });

    const roleMappingStr = roleMappingParts.length > 0 
      ? roleMappingParts.join('；') 
      : '无关联角色垫图';

    // Construct the prompt
    let prompt = `【生成 3×3 九宫格分镜图片，独立镜头不重叠】\n` +
      `角色锁定：关联用户上传的本地角色垫图，按上传顺序绑定角色（${roleMappingStr}），所有镜头角色形象与对应垫图 100% 匹配，不混淆、不变形。\n` +
      `分镜适配：严格按以下 9 个分镜生成，每个格子对应 1 个分镜，景别、动作、场景与分镜脚本完全一致：\n` +
      `${storyboard}\n` +
      `风格要求：整体画面风格统一，色调、光影一致，无单镜头风格突兀；角色特征全程与对应垫图一致，无串色、变形；各镜头无无关元素干扰，核心主体突出。\n` +
      `使用要求：生成前需确认用户已按角色顺序上传垫图，且提示词中已明确「上传顺序 - 角色」的对应关系。`;

    setRowLoadingFlag(rowId, 'grid', true);
    setRowErrorText(rowId, 'grid', undefined);
    updateRow(rowId, (r) => ({ ...r, gridImageUrl: null, gridTaskId: null }));

    try {
      const res = await fetch(`${API_BASE}/api/proxy/nano/generate_task`, {
        method: 'POST',
        headers: buildAuthHeaders(true),
        credentials: 'include',
        body: JSON.stringify({
          model: "nano-banana-2",
          prompt: prompt,
          aspect_ratio: "1:1",
          images: validImages,
          image_size: "1K"
        })
      });

      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw new Error(errText || `Request failed with status ${res.status}`);
      }

      const json = (await res.json()) as NanoTaskCreateResponse;
      const taskId = String(json.task_id || '').trim();
      if (!taskId) {
        throw new Error('NanoBanana 返回缺少任务ID');
      }

      gridPollTaskRef.current[rowId] = taskId;
      updateRow(rowId, (r) => ({ ...r, gridTaskId: taskId }));
      try { await refreshUser(); } catch { /* ignore */ }
      startGridTaskPolling(rowId, taskId);
    } catch (e: any) {
      const msg = e?.message || '生成失败';
      setRowErrorText(rowId, 'grid', msg);
      setRowLoadingFlag(rowId, 'grid', false);
      window.alert(e?.message || '生成失败');
    }
  };

  const handleGenerateVideo = async (rowId: string) => {
    const row = rows.find((r) => r.id === rowId);
    if (!row) return;

    if (!row.gridImageUrl) {
      window.alert('九宫格图像未生成，无法生成视频');
      return;
    }

    if (!String(row.videoScript || '').trim()) {
      window.alert('视频脚本为空，无法生成视频');
      return;
    }

    setRowLoadingFlag(rowId, 'video', true);
    setRowErrorText(rowId, 'video', undefined);
    
    // Update status to generating, and CLEAR previous videoUrl
    updateRow(rowId, (r) => ({ ...r, videoStatus: 'generating', videoProgress: 0, videoUrl: null }));

    try {
      const createRes = await fetch(`${API_BASE}/api/generate/video/sora`, {
        method: 'POST',
        headers: buildAuthHeaders(true),
        body: JSON.stringify({
           url: row.gridImageUrl,
           prompt: row.videoScript,
           aspectRatio: row.aspectRatio,
           model: "sora-2",
           webHook: "-1",
           duration: 10,
           size: "small",
           shutProgress: false
        })
      });

      if (!createRes.ok) {
         const createText = await createRes.text().catch(() => '');
         let createPayload: unknown = createText;
         try {
           createPayload = createText ? JSON.parse(createText) : null;
         } catch {
           createPayload = createText;
         }
         throw new Error(extractApiErrorMessage(createPayload, '启动视频生成任务失败'));
      }

      const createData = await createRes.json();
      const taskId = extractVideoTaskId(createData);

      if (!taskId) {
         throw new Error('未获取到任务ID');
      }

      updateRow(rowId, (r) => ({ ...r, videoSoraId: taskId }));
      // 新增：刷新用户积分
      try { await refreshUser(); } catch { /* ignore */ }

      // Poll for results
      const poll = async () => {
         try {
             // Check if user still on page or row deleted? (Simple version: continues running)
             const queryRes = await fetch(`${API_BASE}/api/generate/video/sora/result`, {
               method: 'POST',
               headers: buildAuthHeaders(true),
               body: JSON.stringify({ id: taskId })
             });
             
             if (!queryRes.ok) {
                 const queryText = await queryRes.text().catch(() => '');
                 let queryPayload: unknown = queryText;
                 try {
                   queryPayload = queryText ? JSON.parse(queryText) : null;
                 } catch {
                   queryPayload = queryText;
                 }
                 throw new Error(extractApiErrorMessage(queryPayload, '生成失败'));
             }

             const queryData = await queryRes.json();
             const normalized = normalizeVideoQueryResponse(queryData);
             
             updateRow(rowId, (r) => ({ ...r, videoProgress: normalized.progress }));

             if (normalized.status === 'succeeded' && normalized.videoUrl) {
                 updateRow(rowId, (r) => ({ ...r, videoUrl: normalized.videoUrl, videoStatus: 'success', videoProgress: 100 }));
                 setRowLoadingFlag(rowId, 'video', false);
                 return true; 
             } else if (normalized.status === 'failed' || normalized.error) {
                 throw new Error(normalized.error || '生成失败');
             }

             return false; 

         } catch (e: any) {
             console.error("Poll error", e);
             throw e;
         }
      };

      const startTime = Date.now();
      const interval = 3000;
      
      const loop = async () => {
           try {
               const done = await poll();
               if (done) return;
               
               if (Date.now() - startTime > 600000) { // 10 min timeout
                   throw new Error('生成超时');
               }
               setTimeout(loop, interval);
             } catch (e: any) {
               const msg = e?.message || '生成失败';
               setRowErrorText(rowId, 'video', msg);
               updateRow(rowId, (r) => ({ ...r, videoStatus: 'failed' }));
               setRowLoadingFlag(rowId, 'video', false);
               try { window.alert(msg); } catch {}
           }
      };
      
      loop();
      
    } catch (e: any) {
      const msg = e?.message || '生成失败';
      setRowErrorText(rowId, 'video', msg);
      updateRow(rowId, (r) => ({ ...r, videoStatus: 'failed' }));
      setRowLoadingFlag(rowId, 'video', false);
      try { window.alert(msg); } catch {}
    }
  };

  const handleSendToAnimation = async (payload: { tweetText: string; storyboardScript: string }) => {
    const storyboard = payload.storyboardScript;
    if (!storyboard.trim()) return;
    if (sendToAnimationLoading) return;

    setSendToAnimationLoading(true);
    setSendToAnimationError(null);

    try {
      const roles = await runRoleVoiceAnalysis(payload.tweetText);
      // 角色音色分析会消耗 1 积分，这里在成功返回后刷新一次用户积分显示
      try { await refreshUser(); } catch { /* ignore */ }
      if (!roles.length) {
        throw new Error('未解析到任何【角色名称】段，请检查角色分析输出格式');
      }

      // 不再调用 /api/proxy/xgai/video-script-generate，改为用角色音色分析结果直接拼接模板提示词
      const videoScript = buildVideoScriptPromptFromRoles(roles);

      // 结果齐全后：对“第一空行”一次性写入 + 跳转到动画制作
      applyAllToFirstEmptyRow({ storyboardScript: storyboard, roles, videoScript });
      setSub('animation');
    } catch (e) {
      const msg = String((e as Error)?.message || e || '');
      const isAuth = /401|unauthorized|forbidden|403/i.test(msg);
      setSendToAnimationError(
        isAuth ? '未登录或登录已过期（请先登录）' : msg || '接口失败（请确认后端在运行且已配置 Key）'
      );
    } finally {
      setSendToAnimationLoading(false);
    }
  };

  const handleSubChange = (next: AiAnimationSubSection) => {
    if (sub === 'script' && next !== 'script') {
      setScriptInstance((v) => v + 1);
    }
    setSub(next);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
    navigate('/login');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#eff6ff' }}>
      <TopNavBar />
      {/* 以下原页内 AppBar 替换为共享 TopNavBar */}
      {/*
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: '#eff6ff',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid #bfdbfe',
          position: 'relative',
          overflow: 'hidden',
          zIndex: 1,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: '-5%',
            zIndex: 0,
            pointerEvents: 'none',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 320'%3E%3Cpath fill='%233b82f6' fill-opacity='0.15' d='M0,128L48,144C96,160,192,192,288,197.3C384,203,480,181,576,181.3C672,181,768,203,864,224C960,245,1056,267,1152,250.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z'%3E%3C/path%3E%3Cpath fill='%2360a5fa' fill-opacity='0.3' d='M0,224L48,213.3C96,203,181,181,288,181.3C384,181,480,203,576,224C672,245,768,267,864,250.7C960,235,1056,181,1152,165.3C1248,149,1344,171,1392,181.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z'%3E%3C/path%3E%3Cpath fill='%23ffffff' fill-opacity='0.6' d='M0,256L48,245.3C96,235,192,213,288,213.3C384,213,480,235,576,245.3C672,256,768,256,864,245.3C960,235,1056,213,1152,208C1248,203,1344,213,1392,218.7L1440,224L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z'%3E%3C/path%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            animation: 'waveMove 20s ease-in-out infinite alternate'
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
            transform: 'skewX(-20deg)',
            animation: `${shimmer} 8s infinite linear`,
            pointerEvents: 'none',
            zIndex: 0
          }
        }}
      >
        <Toolbar sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: '#1e293b', fontWeight: 700 }}>
            Vwin
          </Typography>

          <Box display="flex" alignItems="center" gap={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Button 
                onClick={() => navigate('/home')}
                sx={{ 
                  color: '#ffffff', 
                  fontWeight: 600,
                  minWidth: 'auto',
                  px: 1.5,
                  transition: 'all 0.3s',
                  transform: location.pathname === '/home' ? 'scale(1.2)' : 'scale(1)',
                  textShadow: location.pathname === '/home' 
                    ? '1px 1px 0 #000, 3px 3px 0px rgba(0,0,0,0.5)' 
                    : '1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000',
                  '&:hover': { 
                    bgcolor: 'transparent',
                    transform: 'scale(1.2)',
                    textShadow: '1px 1px 0 #000, 3px 3px 0px rgba(0,0,0,0.5)',
                  }
                }}
              >
                主页
              </Button>
              <Button 
                onClick={() => navigate('/news')}
                sx={{ 
                  color: '#ffffff', 
                  fontWeight: 600,
                  minWidth: 'auto',
                  px: 1.5,
                  transition: 'all 0.3s',
                  transform: location.pathname === '/news' ? 'scale(1.2)' : 'scale(1)',
                  textShadow: location.pathname === '/news' 
                    ? '1px 1px 0 #000, 3px 3px 0px rgba(0,0,0,0.5)' 
                    : '1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000',
                  '&:hover': { 
                    bgcolor: 'transparent',
                    transform: 'scale(1.2)',
                    textShadow: '1px 1px 0 #000, 3px 3px 0px rgba(0,0,0,0.5)',
                  }
                }}
              >
                <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                  优选
                  {location.pathname === '/news' && (
                    <AutoAwesomeIcon 
                      sx={{ 
                        position: 'absolute', 
                        bottom: -2, 
                        right: -14, 
                        fontSize: 14, 
                        color: '#ffd700',
                        filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))'
                      }} 
                    />
                  )}
                </Box>
              </Button>
              <Button 
                onClick={() => navigate('/pricing')}
                sx={{ 
                  color: '#ffffff', 
                  fontWeight: 600,
                  minWidth: 'auto',
                  px: 1.5,
                  transition: 'all 0.3s',
                  transform: location.pathname === '/pricing' ? 'scale(1.2)' : 'scale(1)',
                  textShadow: location.pathname === '/pricing' 
                    ? '1px 1px 0 #000, 3px 3px 0px rgba(0,0,0,0.5)' 
                    : '1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000',
                  '&:hover': { 
                    bgcolor: 'transparent',
                    transform: 'scale(1.2)',
                    textShadow: '1px 1px 0 #000, 3px 3px 0px rgba(0,0,0,0.5)',
                  }
                }}
              >
                会员套餐
              </Button>
            </Box>

            <Box 
              display="flex" 
              alignItems="center" 
              gap={1}
              onMouseEnter={handleMenu}
              sx={{ position: 'relative' }}
            >
              <Box sx={{ p: 0, cursor: 'pointer' }}>
                <Avatar sx={{ bgcolor: '#2563eb', width: 32, height: 32, fontSize: '0.9rem' }}>
                  {loading ? '...' : (user?.username ? user.username.charAt(0).toUpperCase() : 'U')}
                </Avatar>
              </Box>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                MenuListProps={{
                  onMouseLeave: handleClose,
                }}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.08))',
                    mt: 0.5,
                    borderRadius: 0.5,
                    minWidth: 140,
                    bgcolor: 'white',
                  },
                }}
              >
                <Box sx={{ px: 2, py: 1.2 }}>
                  <Typography variant="body1" sx={{ color: '#1e293b', fontWeight: 700, mb: 0.3, fontSize: '0.95rem' }}>
                    {loading ? '加载中...' : (user?.username || '未登录用户')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem', display: 'block' }}>
                    {loading ? '...' : (user?.email || '未绑定邮箱')}
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 0 }} />

                <Box sx={{ px: 2, py: 1.5 }}>
                  {(() => {
                    const roleRaw = (user?.role || 'free').toString().toLowerCase();
                    const roleMap: Record<string, string> = {
                      free: 'Free',
                      creator: 'Creator',
                      business: 'Business',
                    };
                    const roleDisplay = (roleMap[roleRaw] || 'Free') + ' 计划';
                    return (
                      <Box sx={{
                        bgcolor: '#f8fafc',
                        px: 1.5,
                        py: 0.8,
                        borderRadius: 1.5,
                        border: '1px solid #f1f5f9'
                      }}>
                        <Box sx={{ mb: 0.8 }}>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#0f172a' }}>{roleDisplay}</Typography>
                        </Box>
                        <Divider sx={{ my: 0.6, borderColor: '#e2e8f0' }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 600 }}>积分</Typography>
                          <Typography sx={{ color: '#2563eb', fontSize: '0.85rem', fontWeight: 800 }}>{user?.points || 0}</Typography>
                        </Box>
                      </Box>
                    );
                  })()}
                </Box>
                
                <MenuItem onClick={() => { handleClose(); navigate('/profile'); }} sx={{ py: 0.8, px: 2, fontSize: '0.75rem', minHeight: 'auto' }}>
                  <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500, fontSize: '0.75rem' }}>个人中心（积分购买）</Typography>
                </MenuItem>
                <MenuItem onClick={handleLogout} sx={{ py: 0.8, px: 2, fontSize: '0.75rem', minHeight: 'auto' }}>
                  <Typography variant="body2" sx={{ color: '#ef4444', fontWeight: 500, fontSize: '0.75rem' }}>退出登录</Typography>
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      */}

      {/* Main Content Area */}
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'flex-start',
          flexGrow: 1,
          pt: 12, // Position it higher up
          minHeight: 'calc(100vh - 64px)', // Subtract AppBar height
        }}
      >
        {/* 保持 LogoCarousel 全宽，以保留两侧的背景/渐隐效果 */}
        <Box sx={{ width: '100%' }}>
          <LogoCarousel
            activeIndex={topTab === 'animation' ? 0 : topTab === 'video' ? 1 : 2}
            navigateOnSelect={false}
            persistKey="main"
            onSelect={(index) => {
              if (index === 0) setTopTab('animation');
              if (index === 1) setTopTab('video');
              if (index === 2) setTopTab('doubao');
            }}
          />
        </Box>

        {/* 图二红色区域：承载图一页面内容（走居中容器） */}
        <Box
          sx={{
            width: { xs: '98%', sm: '96%', md: '94%', lg: '92%', xl: '90%' },
            maxWidth: '1920px',
            mx: 'auto',
            px: { xs: 0.5, sm: 1, md: 2 },
          }}
        >
          {topTab === 'doubao' ? (
            <DoubaoPanel />
          ) : topTab === 'video' ? (
            <Box sx={{ mt: 10, mb: 3, width: '100%' }}>
              <AiVideoEditPage embedded />
            </Box>
          ) : (
            <>
              <Box sx={{ mt: 10, mb: 3, width: '100%' }}>
                <AiAnimationSubNav value={sub} onChange={handleSubChange} />
              </Box>

              {sub === 'role' ? (
                <Box sx={{ mb: 3 }}>
                  <RoleImageGeneratorPanel />
                </Box>
              ) : sub === 'script' ? (
                <Box sx={{ mb: 3 }}>
                  <ScriptAnalysisPanel
                    key={`script-${scriptInstance}`}
                    onSendToAnimation={handleSendToAnimation}
                    sendToAnimationLoading={sendToAnimationLoading}
                    sendToAnimationError={sendToAnimationError}
                  />
                </Box>
              ) : (
                <Box>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 0,
                      bgcolor: 'rgba(255, 255, 255, 0.75)',
                      backdropFilter: 'blur(20px) saturate(180%)',
                      border: '1px solid rgba(255, 255, 255, 0.6)',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      width: '100%',
                      minHeight: { xs: 220, md: 260 },
                    }}
                  >
                <Box
                  sx={{
                    width: '100%',
                    borderLeft: gridBorder,
                    borderRight: gridBorder,
                    borderTop: gridBorder,
                    borderBottom: gridBorder,
                    boxSizing: 'border-box',
                  }}
                >
                  {/* 表头 */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: gridCols, width: '100%' }}>
                    {headers.map((t, idx) => (
                      <Box
                        key={t}
                        sx={{
                          textAlign: 'center',
                          fontWeight: 900,
                          color: '#0f172a',
                          py: 2,
                          borderBottom: gridBorder,
                          borderRight: idx === headers.length - 1 ? 'none' : gridBorder,
                          bgcolor: 'rgba(255,255,255,0.55)',
                        }}
                      >
                        {t}
                      </Box>
                    ))}
                  </Box>

                  {/* 行 */}
                  <Box sx={{ width: '100%' }}>
                    {rows.map((row, rowIdx) => {
                      const canDelete = rowIdx > 0;
                      const canAddRole = row.roleCards.length < 11;
                      const isRolesLoading = Boolean(rowLoading[row.id]?.roles);
                      const isVideoLoading = Boolean(rowLoading[row.id]?.video);
                      const isClearDisabled = isRowBusy(rowLoading[row.id]);
                      const rolesError = rowError[row.id]?.roles;
                      const videoError = rowError[row.id]?.video;
                      const isRoleExpanded = Boolean(rowRoleExpanded[row.id]);
                      const accent = theme.palette.primary.main;
                      const collapsedRowMinHeight = getCollapsedRowMinHeight(canDelete);
                      const expandControlPlacement = getExpandControlPlacement();
                      const rightActionColumnMetrics = getRightActionColumnMetrics(canDelete);
                      const primaryBtnSx = {
                        minWidth: 86,
                        height: 34,
                        px: 2.2,
                        fontWeight: 900,
                        borderRadius: '8px',
                        textTransform: 'none',
                        letterSpacing: '0.02em',
                        boxShadow: 'none',
                        background: accent,
                        '&:hover': {
                          background: alpha(accent, 0.9),
                          boxShadow: 'none',
                        },
                        '&:active': {
                          transform: 'translateY(0.5px)',
                        },
                      } as const;

                      const secondaryBtnSx = {
                        minWidth: 86,
                        height: 34,
                        px: 2.2,
                        fontWeight: 800,
                        borderRadius: '10px',
                        textTransform: 'none',
                        letterSpacing: '0.02em',
                        border: '1.5px solid',
                        borderColor: 'rgba(255, 255, 255, 0.9)',
                        color: '#1e293b',
                        bgcolor: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 247, 255, 0.9) 100%)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: `
                          0 2px 8px rgba(59, 130, 246, 0.15),
                          0 4px 16px rgba(59, 130, 246, 0.1),
                          inset 0 1px 0 rgba(255, 255, 255, 0.8)
                        `,
                        position: 'relative',
                        overflow: 'visible',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          inset: '-1.5px',
                          borderRadius: '10px',
                          padding: '1.5px',
                          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.4) 0%, rgba(147, 197, 253, 0.2) 50%, rgba(59, 130, 246, 0.4) 100%)',
                          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                          WebkitMaskComposite: 'xor',
                          maskComposite: 'exclude',
                          opacity: 0.6,
                          zIndex: -1,
                        },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          background: 'radial-gradient(circle, #3b82f6 0%, #60a5fa 50%, #93c5fd 100%)',
                          boxShadow: `
                            0 0 12px rgba(59, 130, 246, 0.8),
                            0 0 24px rgba(59, 130, 246, 0.4),
                            0 0 36px rgba(59, 130, 246, 0.2)
                          `,
                          animation: 'riceBorder 3s ease-in-out infinite, colorShift 12s ease-in-out infinite',
                          pointerEvents: 'none',
                          zIndex: 10,
                          filter: 'brightness(1.2)',
                        },
                        '&:hover': {
                          borderColor: 'rgba(255, 255, 255, 1)',
                          transform: 'translateY(-2px)',
                          boxShadow: `
                            0 4px 16px rgba(59, 130, 246, 0.25),
                            0 8px 32px rgba(59, 130, 246, 0.15),
                            inset 0 1px 0 rgba(255, 255, 255, 1)
                          `,
                          '&::before': {
                            opacity: 1,
                          },
                        },
                        '&:active': {
                          transform: 'translateY(0px)',
                        },
                      } as const;

                      const dangerBtnSx = {
                        minWidth: 86,
                        height: 34,
                        px: 2.2,
                        fontWeight: 900,
                        borderRadius: '8px',
                        textTransform: 'none',
                        letterSpacing: '0.01em',
                        borderWidth: 2,
                        borderColor: alpha(theme.palette.text.primary, 0.18),
                        color: theme.palette.text.secondary,
                        bgcolor: 'rgba(255,255,255,0.35)',
                        '&:hover': {
                          borderColor: alpha(theme.palette.text.primary, 0.26),
                          bgcolor: 'rgba(255,255,255,0.55)',
                        },
                      } as const;

                      return (
                        <Box
                          key={row.id}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: gridCols,
                            width: '100%',
                            height: isRoleExpanded ? 'auto' : collapsedRowMinHeight,
                            minHeight: collapsedRowMinHeight,
                            bgcolor: 'rgba(255, 255, 255, 0.35)',
                            borderBottom: rowIdx === rows.length - 1 ? 'none' : gridBorder,
                            position: 'relative',
                          }}
                        >
                          {/* 分镜脚本 */}
                          <Box
                            onWheel={(e) => e.stopPropagation()}
                            sx={{
                              borderRight: gridBorder,
                              px: { xs: 1.0, md: 1.2 },
                              pt: { xs: 1.0, md: 1.2 },
                              pb: { xs: 1.4, md: 1.6 },
                              display: 'flex',
                              alignItems: 'stretch',
                              overflow: 'hidden',
                            }}
                          >
                            <InputBase
                              value={row.storyboardScript}
                              onChange={(e) => setStoryboardScript(row.id, e.target.value)}
                              multiline
                              minRows={6}
                              maxRows={10}
                              placeholder="请输入分镜脚本"
                              sx={{
                                width: '100%',
                                height: '100%',
                                fontSize: 13,
                                fontWeight: 500,
                                color: theme.palette.text.secondary,
                                '& textarea': {
                                  height: '100% !important',
                                  overflow: 'auto !important',
                                  overflowX: 'hidden !important',
                                  overscrollBehavior: 'contain',
                                  scrollbarGutter: 'stable',
                                  paddingBottom: '10px',
                                  resize: 'none',
                                  lineHeight: 1.35,
                                  color: theme.palette.text.secondary,
                                },
                                '& textarea::placeholder': {
                                  color: alpha(theme.palette.text.secondary, 0.8),
                                  opacity: 1,
                                },
                              }}
                            />
                          </Box>

                          {/* 角色 */}
                          <Box
                            sx={{
                              borderRight: gridBorder,
                              px: { xs: 0.9, md: 1.2 },
                              py: { xs: 1.1, md: 1.5 },
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1.2,
                              overflow: 'hidden',
                              position: 'relative',
                            }}
                          >
                            {isRolesLoading ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.8,
                                  color: alpha(theme.palette.text.secondary, 0.92),
                                  mb: 0.6,
                                  userSelect: 'none',
                                }}
                              >
                                <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'inherit' }}>
                                  角色/音色分析中
                                </Typography>
                                <JumpingDots color={alpha(theme.palette.text.secondary, 0.75)} />
                              </Box>
                            ) : null}
                            {!isRolesLoading && rolesError ? (
                              <Typography sx={{ fontSize: 12, fontWeight: 800, color: theme.palette.error.main, mb: 0.6 }}>
                                {rolesError}
                              </Typography>
                            ) : null}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1.4,
                                overflow: isRoleExpanded ? 'visible' : 'hidden',
                                flex: '1 1 auto',
                                pb: 2.2,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  gap: 1.2,
                                  minHeight: 86,
                                  flex: '1 1 auto',
                                  minWidth: 0,
                                  pr: 1,
                                }}
                              >
                                {row.roleCards.map((card, cardIdx) => {
                                  const inputKey = `${row.id}:${card.id}`;
                                  const canDeleteRole = row.roleCards.length > 1;
                                  const roleNo = cardIdx + 1;
                                  return (
                                    <Box key={card.id} sx={{ display: 'flex', flexDirection: 'column', gap: 1.0 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.0, maxWidth: '100%' }}>
                                        <Typography
                                          sx={{
                                            fontWeight: 900,
                                            color: accent,
                                            flex: '0 0 auto',
                                            minWidth: 56,
                                            whiteSpace: 'nowrap',
                                            lineHeight: 1.1,
                                            textAlign: 'left',
                                            alignSelf: 'center',
                                            fontSize: '13px',
                                          }}
                                        >
                                          角色{roleNo}:
                                        </Typography>

                                        <Box
                                          onClick={() => fileInputRefs.current[inputKey]?.click()}
                                          role="button"
                                          aria-label="上传人物图片"
                                          sx={{
                                            width: 56,
                                            height: 64,
                                            borderRadius: 1,
                                            border: 2,
                                            borderStyle: 'dashed',
                                            borderColor: alpha(accent, 0.55),
                                            bgcolor: `linear-gradient(180deg, ${alpha(accent, 0.10)} 0%, rgba(255,255,255,0.0) 100%)`,
                                            cursor: 'pointer',
                                            overflow: 'hidden',
                                            flex: '0 0 auto',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative',
                                            '&:hover': {
                                              borderColor: alpha(accent, 0.75),
                                              bgcolor: `linear-gradient(180deg, ${alpha(accent, 0.14)} 0%, rgba(255,255,255,0.0) 100%)`,
                                              '& .delete-image-btn': { opacity: 1 },
                                            },
                                          }}
                                        >
                                          <input
                                            ref={(el) => {
                                              fileInputRefs.current[inputKey] = el;
                                            }}
                                            type="file"
                                            accept="image/*"
                                            style={{ display: 'none' }}
                                            onChange={async (e) => {
                                              const file = e.target.files?.[0];
                                              if (!file) return;
                                              await setRoleImage(row.id, card.id, file);
                                              e.target.value = '';
                                            }}
                                          />
                                          {card.imageDataUrl ? (
                                            <>
                                              <Box
                                                component="img"
                                                src={card.imageDataUrl}
                                                alt=""
                                                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                              />
                                              <Box
                                                className="delete-image-btn"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  updateRow(row.id, (r) => ({
                                                    ...r,
                                                    roleCards: r.roleCards.map((c) => (c.id === card.id ? { ...c, imageDataUrl: null } : c)),
                                                  }));
                                                }}
                                                sx={{
                                                  position: 'absolute',
                                                  top: 0,
                                                  right: 0,
                                                  width: 16,
                                                  height: 16,
                                                  bgcolor: 'rgba(0,0,0,0.5)',
                                                  color: '#fff',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  borderRadius: '50%',
                                                  cursor: 'pointer',
                                                  opacity: 0,
                                                  transition: 'all 0.2s',
                                                  zIndex: 2,
                                                  '&:hover': {
                                                    bgcolor: theme.palette.error.main,
                                                  },
                                                }}
                                              >
                                                <CloseIcon sx={{ fontSize: 11 }} />
                                              </Box>
                                            </>
                                          ) : (
                                            <Box
                                              sx={{
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 0.25,
                                                color: alpha(accent, 0.9),
                                                userSelect: 'none',
                                              }}
                                            >
                                              <PersonAddAltOutlinedIcon sx={{ fontSize: 24, opacity: 0.92 }} />
                                              <Typography sx={{ fontSize: 10, fontWeight: 900, lineHeight: 1 }}>
                                                上传人物
                                              </Typography>
                                            </Box>
                                          )}
                                        </Box>

                                        <Box
                                          sx={{
                                            width: 76,
                                            height: 30,
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: alpha(accent, 0.35),
                                            bgcolor: alpha(theme.palette.background.paper, 0.92),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            px: 0.5,
                                            flex: '0 0 auto',
                                            transition: 'border-color 140ms ease, box-shadow 140ms ease',
                                            '&:focus-within': {
                                              borderColor: alpha(accent, 0.75),
                                              boxShadow: `0 0 0 2px ${alpha(accent, 0.14)}`,
                                            },
                                          }}
                                        >
                                          <InputBase
                                            value={card.name}
                                            onChange={(e) => setRoleName(row.id, card.id, e.target.value)}
                                            sx={{
                                              fontSize: 12,
                                              fontWeight: 800,
                                              width: '100%',
                                              '& input': { textAlign: 'center', p: 0 },
                                            }}
                                          />
                                        </Box>

                                        <Tooltip title="减少角色" placement="top" arrow>
                                          <Box component="span" sx={{ display: 'inline-flex', pointerEvents: 'auto' }}>
                                            <IconButton
                                              size="small"
                                              aria-label="减少角色"
                                              title="减少角色"
                                              onClick={() => deleteRoleCard(row.id, card.id)}
                                              disabled={!canDeleteRole}
                                              sx={{
                                                width: 28,
                                                height: 28,
                                                borderRadius: 1,
                                                border: 'none',
                                                boxShadow: 'none',
                                                outline: 'none',
                                                p: 0,
                                                ml: -0.5,
                                                color: accent,
                                                bgcolor: 'transparent',
                                                flex: '0 0 auto',
                                                '&.Mui-focusVisible': {
                                                  outline: 'none',
                                                  boxShadow: 'none',
                                                },
                                                '&:focus': {
                                                  outline: 'none',
                                                },
                                              }}
                                            >
                                              <Box
                                                component="img"
                                                src={`${process.env.PUBLIC_URL || ''}/reduce.svg`}
                                                alt="减少角色"
                                                sx={{ width: 16, height: 16, display: 'block' }}
                                              />
                                            </IconButton>
                                          </Box>
                                        </Tooltip>

                                        {/* 一键添加角色图片图标 */}
                                        <Tooltip title="一键添加角色图片" placement="top" arrow>
                                          <Box component="span" sx={{ display: 'inline-flex', pointerEvents: 'auto' }}>
                                            <IconButton
                                              size="small"
                                              aria-label="一键添加角色图片"
                                              onClick={() => handleOpenRoleDescDialog(row.id, card.id)}
                                              sx={{
                                                width: 28,
                                                height: 28,
                                                borderRadius: 1,
                                                border: 'none',
                                                boxShadow: 'none',
                                                outline: 'none',
                                                p: 0,
                                                color: accent,
                                                bgcolor: 'transparent',
                                                flex: '0 0 auto',
                                                '&.Mui-focusVisible': {
                                                  outline: 'none',
                                                  boxShadow: 'none',
                                                },
                                                '&:focus': {
                                                  outline: 'none',
                                                },
                                              }}
                                            >
                                              <Box
                                                component="img"
                                                src={`${process.env.PUBLIC_URL || ''}/add_tupian.svg`}
                                                alt="一键添加角色图片"
                                                sx={{ width: 16, height: 16, display: 'block' }}
                                              />
                                            </IconButton>
                                          </Box>
                                        </Tooltip>
                                      </Box>

                                      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.0 }}>
                                        <Typography
                                          sx={{
                                            fontWeight: 900,
                                            color: accent,
                                            flex: '0 0 auto',
                                            minWidth: 56,
                                            whiteSpace: 'nowrap',
                                            lineHeight: 1.1,
                                            fontSize: '13px',
                                            pb: '2px',
                                          }}
                                        >
                                          音色:
                                        </Typography>
                                        <Box
                                          sx={{
                                            minHeight: 34,
                                            borderRadius: 0,
                                            borderTop: 'none',
                                            borderLeft: 'none',
                                            borderRight: 'none',
                                            borderBottom: `2px solid ${accent}`,
                                            bgcolor: 'transparent',
                                            px: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            flex: '1 1 auto',
                                            minWidth: 0,
                                            maxWidth: 260,
                                          }}
                                        >
                                          <InputBase
                                            value={card.voice}
                                            onChange={(e) => setRoleVoice(row.id, card.id, e.target.value)}
                                            multiline
                                            sx={{
                                              width: '100%',
                                              fontSize: 13,
                                              wordBreak: 'break-word',
                                              '& input': { p: 0 },
                                              '& textarea': { p: 0, lineHeight: 1.2 },
                                            }}
                                          />
                                        </Box>
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Box>

                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 1.1,
                                  pt: 0.2,
                                  flex: '0 0 auto',
                                  zIndex: 2,
                                }}
                              >
                                <Tooltip title="添加角色" placement="top" arrow>
                                  <Box component="span" sx={{ display: 'inline-flex' }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => addRoleCard(row.id)}
                                      disabled={!canAddRole}
                                      aria-label="添加角色"
                                      sx={{
                                        width: 38,
                                        height: 28,
                                        borderRadius: 1,
                                        border: 0,
                                        color: accent,
                                        bgcolor: 'transparent',
                                        '&:hover': { bgcolor: 'transparent' },
                                        '&.Mui-focusVisible': { outline: 'none' },
                                      }}
                                    >
                                      <Box
                                        component="img"
                                        src={`${process.env.PUBLIC_URL}/add_2.svg`}
                                        alt="添加角色"
                                        sx={{ width: 16, height: 16, display: 'block' }}
                                      />
                                    </IconButton>
                                  </Box>
                                </Tooltip>
                              </Box>
                            </Box>

                            {/* 音色已改为每个角色单独一行 */}
                          </Box>

                          {/* 操作（左） */}
                          <Box
                            sx={{
                              borderRight: gridBorder,
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'center',
                              pt: 3.2,
                            }}
                          >
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.6, alignItems: 'center' }}>
                              <Tooltip title="消耗4积分" arrow placement="top" disableInteractive>
                                <Box sx={{ display: 'flex' }}>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    sx={primaryBtnSx}
                                    disabled={!!row.gridImageUrl || rowLoading[row.id]?.grid}
                                    onClick={() => handleGenerateGridImages(row.id)}
                                  >
                                    {rowLoading[row.id]?.grid ? '...' : '生成'}
                                  </Button>
                                </Box>
                              </Tooltip>
                              
                              <Tooltip title="消耗4积分" arrow placement="bottom" disableInteractive>
                                <Box sx={{ display: 'flex' }}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    sx={secondaryBtnSx}
                                    disabled={rowLoading[row.id]?.grid}
                                    onClick={() => handleGenerateGridImages(row.id)}
                                  >
                                    重新生成
                                  </Button>
                                </Box>
                              </Tooltip>
                            </Box>
                          </Box>

                          {/* 九宫格图像 */}
                          <Box
                            sx={{
                              borderRight: gridBorder,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              p: 1,
                              overflow: 'hidden',
                              position: 'relative',
                            }}
                          >
                            {rowLoading[row.id]?.grid && (
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                <JumpingDots color={theme.palette.primary.main} />
                                <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary }}>
                                  生成中...
                                </Typography>
                              </Box>
                            )}
                            
                            {!rowLoading[row.id]?.grid && row.gridImageUrl && (
                              <Box
                                component="img"
                                src={row.gridImageUrl}
                                alt="九宫格图像"
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain',
                                  borderRadius: 1,
                                }}
                              />
                            )}

                            {!rowLoading[row.id]?.grid && rowError[row.id]?.grid && (
                              <Typography sx={{ fontSize: 12, color: theme.palette.error.main, textAlign: 'center', p: 1 }}>
                                {rowError[row.id]?.grid}
                              </Typography>
                            )}
                          </Box>

                          {/* 视频脚本 */}
                          <Box
                            onWheel={(e) => e.stopPropagation()}
                            sx={{
                              borderRight: gridBorder,
                              px: { xs: 1.0, md: 1.2 },
                              pt: { xs: 1.0, md: 1.2 },
                              pb: { xs: 1.4, md: 1.6 },
                              display: 'flex',
                              alignItems: 'stretch',
                              overflow: 'hidden',
                              position: 'relative',
                            }}
                          >
                            {isVideoLoading && !String(row.videoScript || '').trim() ? (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 10,
                                  left: 12,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.8,
                                  pointerEvents: 'none',
                                  color: alpha(theme.palette.text.secondary, 0.9),
                                  userSelect: 'none',
                                  zIndex: 1,
                                }}
                              >
                                <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'inherit' }}>
                                  视频脚本生成中
                                </Typography>
                                <JumpingDots color={alpha(theme.palette.text.secondary, 0.75)} />
                              </Box>
                            ) : null}
                            {!isVideoLoading && !String(row.videoScript || '').trim() && videoError ? (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 10,
                                  left: 12,
                                  display: 'flex',
                                  alignItems: 'center',
                                  pointerEvents: 'none',
                                  userSelect: 'none',
                                  zIndex: 1,
                                }}
                              >
                                <Typography sx={{ fontSize: 12, fontWeight: 800, color: theme.palette.error.main }}>
                                  {videoError}
                                </Typography>
                              </Box>
                            ) : null}
                            <InputBase
                              value={row.videoScript}
                              onChange={(e) => setVideoScript(row.id, e.target.value)}
                              multiline
                              minRows={6}
                              maxRows={10}
                              placeholder="请输入视频脚本"
                              sx={{
                                width: '100%',
                                height: '100%',
                                fontSize: 13,
                                fontWeight: 500,
                                color: theme.palette.text.secondary,
                                '& textarea': {
                                  height: '100% !important',
                                  overflow: 'auto !important',
                                  overscrollBehavior: 'contain',
                                  scrollbarGutter: 'stable',
                                  paddingBottom: '10px',
                                  resize: 'none',
                                  lineHeight: 1.35,
                                  color: theme.palette.text.secondary,
                                },
                                '& textarea::placeholder': {
                                  color: alpha(theme.palette.text.secondary, 0.8),
                                  opacity: 1,
                                },
                              }}
                            />
                          </Box>

                          {/* 生成视频 */}
                          <Box
                            sx={{
                              borderRight: gridBorder,
                              p: 0.5,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              position: 'relative',
                            }}
                          >
                            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', mb: 0.5, mt: 0.5, mr: 0.5 }}>
                              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center' }}>
                                {(['9:16', '16:9'] as const).map((ratio) => {
                                  const selected = row.aspectRatio === ratio;
                                  return (
                                    <Box
                                      key={ratio}
                                      role="button"
                                      aria-label={`选择比例 ${ratio}`}
                                      onClick={() => updateRow(row.id, (r) => ({ ...r, aspectRatio: ratio }))}
                                      sx={{
                                        width: 40,
                                        height: 18,
                                        borderRadius: 0.5,
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        border: `1px solid ${selected ? theme.palette.primary.main : alpha(theme.palette.text.secondary, 0.35)}`,
                                        color: selected ? theme.palette.primary.main : alpha(theme.palette.text.secondary, 0.92),
                                        bgcolor: selected ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.background.paper, 0.35),
                                        transition: 'all 120ms ease',
                                        '&:hover': {
                                          borderColor: selected ? theme.palette.primary.main : alpha(theme.palette.text.secondary, 0.6),
                                          bgcolor: selected ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.background.paper, 0.5),
                                        },
                                      }}
                                    >
                                      {ratio}
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                            
                            <Box sx={{ 
                                flex: 1, 
                                width: '100%', 
                                bgcolor: row.videoUrl ? '#000' : 'rgba(0,0,0,0.03)', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                borderRadius: 1,
                                overflow: 'hidden',
                                position: 'relative',
                                minHeight: 0,
                                mb: 0.5
                            }}>
                                {row.videoUrl ? (
                                    <video 
                                        src={row.videoUrl} 
                                        controls 
                                        style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                                    />
                                ) : (
                                   rowLoading[row.id]?.video ? (
                                       <Box sx={{ 
                                           width: '100%', 
                                           height: '100%', 
                                           display: 'flex', 
                                           alignItems: 'center', 
                                           justifyContent: 'center', 
                                           position: 'relative',
                                           flexDirection: 'column'
                                       }}>
                                           {/* 左上角进度条 */}
                                           {row.videoProgress !== undefined && (
                                               <Box sx={{
                                                   position: 'absolute',
                                                   top: 8,
                                                   left: 8,
                                                   zIndex: 1
                                               }}>
                                                   <Typography sx={{ fontSize: 12, fontWeight: 'bold', color: theme.palette.primary.main }}>
                                                       {row.videoProgress}%
                                                   </Typography>
                                               </Box>
                                           )}
                                           
                                           {/* 居中文字+跳动动画 */}
                                           <Typography sx={{ color: theme.palette.text.secondary, fontWeight: 'bold', fontSize: 13 }}>
                                               生成中
                                               <span className="bouncing-dot">.</span>
                                               <span className="bouncing-dot">.</span>
                                               <span className="bouncing-dot">.</span>
                                           </Typography>
                                       </Box>
                                   ) : (
                                    // 失败或空白时显示错误信息
                                    (row.videoStatus === 'failed' && rowError[row.id]?.video) ? (
                                      <Typography sx={{ color: theme.palette.error.main, fontSize: 12, fontWeight: 800, p: 1, textAlign: 'center' }}>
                                        {rowError[row.id]?.video}
                                      </Typography>
                                    ) : null
                                   )
                                )}
                            </Box>
                          </Box>

                          {/* 操作（右） */}
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              pt: rightActionColumnMetrics.topPadding,
                              pb: 3,
                              minHeight: '100%',
                              position: 'relative',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: rightActionColumnMetrics.buttonGap,
                              }}
                            >
                              <Tooltip title="消耗25积分" arrow placement="top" disableInteractive>
                                <Box sx={{ display: 'flex' }}>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    sx={primaryBtnSx}
                                    onClick={() => handleGenerateVideo(row.id)}
                                    disabled={!!row.videoUrl || rowLoading[row.id]?.video}
                                  >
                                    {rowLoading[row.id]?.video ? '...' : '生成'}
                                  </Button>
                                </Box>
                              </Tooltip>

                              <Tooltip title="消耗25积分" arrow placement="bottom" disableInteractive>
                                <Box sx={{ display: 'flex' }}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    sx={secondaryBtnSx}
                                    onClick={() => handleGenerateVideo(row.id)}
                                    disabled={rowLoading[row.id]?.video}
                                  >
                                    重新生成
                                  </Button>
                                </Box>
                              </Tooltip>
                              <Tooltip title="清除这一行的全部内容" arrow placement="bottom" disableInteractive>
                                <Box sx={{ display: 'flex' }}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    sx={dangerBtnSx}
                                    onClick={() => clearRow(row.id)}
                                    disabled={isClearDisabled}
                                  >
                                    清除行
                                  </Button>
                                </Box>
                              </Tooltip>
                              {canDelete ? (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => deleteRow(row.id)}
                                  sx={dangerBtnSx}
                                >
                                  删除行
                                </Button>
                              ) : (
                                <Box sx={{ height: 30 }} />
                              )}
                            </Box>
                            <Box
                              onClick={() => toggleRowRoleExpanded(row.id)}
                              role="button"
                              aria-label={isRoleExpanded ? '收缩角色内容' : '展开角色内容'}
                              sx={{
                                position: 'absolute',
                                right: `${expandControlPlacement.right}px`,
                                bottom: `${expandControlPlacement.bottom}px`,
                                cursor: 'pointer',
                                userSelect: 'none',
                                color: alpha(theme.palette.text.secondary, 0.85),
                                fontSize: 12,
                                fontWeight: 800,
                                lineHeight: 1,
                                minHeight: 18,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '&:hover': {
                                  color: alpha(theme.palette.text.secondary, 1),
                                },
                              }}
                            >
                              {isRoleExpanded ? '收缩' : '展开'}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Paper>

              {/* 容器外边底部的按钮：只保留图标（无白色方块底） */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Tooltip title="增加视频栏" placement="top">
                  <Box
                    role="button"
                    aria-label="增加视频栏"
                    onClick={addRow}
                    sx={{
                      width: 22,
                      height: 22,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover img': { opacity: 0.85 },
                    }}
                  >
                    <Box
                      component="img"
                      src={`${process.env.PUBLIC_URL || ''}/add.svg`}
                      alt="add"
                      sx={{ width: 18, height: 18, display: 'block' }}
                    />
                  </Box>
                </Tooltip>
              </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* 角色形象描述弹窗 */}
      <Dialog
        open={roleDescDialogOpen}
        onClose={handleCloseRoleDescDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ m: 0, px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
              角色形象描述
            </Typography>
            <Tooltip title="消耗1积分" arrow placement="top">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={isGeneratingRoleDesc ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                  onClick={() => {
                       if (currentEditingRole) {
                           handleGenerateRoleDesc(currentEditingRole.rowId, currentEditingRole.roleId);
                       }
                  }}
                  disabled={isGeneratingRoleDesc}
                  sx={{
                    fontSize: '0.75rem',
                    minWidth: 'auto',
                    px: 1.5,
                    py: 0.2,
                    height: 26,
                    borderRadius: 4
                  }}
                >
                  {isGeneratingRoleDesc ? '生成中...' : '一键生成'}
                </Button>
              </span>
            </Tooltip>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleCloseRoleDescDialog}
            size="small"
            sx={{
              color: (theme) => theme.palette.grey[500],
              p: 0.5
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <TextField
            autoFocus
            margin="none"
            id="role-desc"
            label=""
            type="text"
            fullWidth
            multiline
            minRows={10}
            variant="standard"
            placeholder="请输入角色形象描述词"
            value={roleDescText}
            onChange={(e) => setRoleDescText(e.target.value)}
            InputProps={{
              disableUnderline: true,
              style: { fontSize: 13 }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, pt: 0 }}>
          <Tooltip title="消耗4积分" arrow placement="top">
            <span>
              <Button
                variant="text"
                onClick={handleGenerateRoleImage}
                disabled={isGeneratingRoleImage || !roleDescText.trim()}
                sx={{
                  color: '#666666',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  '&:hover': {
                     color: '#1976d2',
                     bgcolor: 'transparent',
                  }
                }}
              >
                {isGeneratingRoleImage ? '生成中...' : '生成'}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {/* 积分检查弹窗 */}
      <PointsCheckDialog
        open={showDialog}
        onClose={closeDialog}
        isZeroPoints={dialogProps.isZeroPoints}
      />
    </Box>
  );
};

export default NewsPage;
